import { useSelector } from 'react-redux';

import type { ReviewsSummaryProps } from 'src/components/organisms/ReviewsSummary/components/ReviewsSummary';
import { getAsAdvertisedPercentage } from 'src/components/organisms/ReviewsSummary/helpers/getAsAdvertisedPercentage';
import {
  trackAiSummaryImpression,
  trackMostLovedImpression,
  trackReviewsSummaryImpression,
} from 'src/components/organisms/ReviewsSummary/tracking/reviewsSummaryEvents';
import { selectProductAttributes } from 'src/redux/product/selectors';
import {
  selectAiSummary,
  selectListAverageRating,
  selectProductInsights,
  selectRatingBreakdown,
} from 'src/redux/reviews/selectors';
import type { ReviewsSummaryAiSummary } from 'src/redux/reviews/types';

/**
 * View model for the reviews summary. Owns the reads across the product and
 * reviews slices, the minimum-responses rule on the "% as advertised" score
 * and the three impressions, so the presentational section stays pure props.
 *
 * Returns null when there is no AI summary, which is how the section is
 * omitted from the page: without it there is no summary to show, only a rating
 * the page already gives elsewhere.
 */
export interface ReviewsSummaryViewModel extends Omit<ReviewsSummaryProps, 'aiSummary'> {
  aiSummary: ReviewsSummaryAiSummary;
}

export const useReviewsSummaryViewModel = (): ReviewsSummaryViewModel | null => {
  const aiSummary = useSelector(selectAiSummary);
  const productInsights = useSelector(selectProductInsights);
  const attributes = useSelector(selectProductAttributes);
  const ratingBreakdown = useSelector(selectRatingBreakdown);
  const listAverageRating = useSelector(selectListAverageRating);

  if (!aiSummary) {
    return null;
  }

  return {
    aiSummary,
    asAdvertisedPercentage: getAsAdvertisedPercentage(productInsights),
    /**
     * Feefo's average, falling back to the review list's own. Both settle after
     * hydration, so the rating appears once one of them lands rather than in
     * the server markup; the AI summary above it is server-rendered either way.
     *
     * The page's `aggregateRating` structured data deliberately does not follow
     * this: it is built during SSR, where neither of these exists, so it stays
     * on UPS. See getAggregateRatingJSONLDSchema.
     */
    averageRating: ratingBreakdown?.averageRating || listAverageRating || null,
    isMostLoved: Boolean(attributes?.highlyRated),
    onAiSummaryVisible: trackAiSummaryImpression,
    onMostLovedVisible: trackMostLovedImpression,
    onVisible: trackReviewsSummaryImpression,
  };
};
